import React, { useState } from 'react';
import { Platform, StyleSheet } from 'react-native';

import { Box, Text } from '@components/Restyle';
import theme from '@themes/theme';

// Hover tooltip for web viewer toolbar buttons. Extracted out of
// MediaViewer.web.tsx (TA-10293) so ImagesPreviewNotLogin.web.tsx (the
// public `Copy link to share` viewer) can show the same hover affordance on
// its download/zoom controls without duplicating the bubble styling.
export const ToolbarTooltip: React.FC<{
  label: string;
  customContent?: React.ReactNode;
  forceOpen?: boolean;
  children: React.ReactElement;
}> = ({ label, customContent, forceOpen, children }) => {
  const [hovered, setHovered] = useState(false);
  const open = forceOpen || hovered;
  if (Platform.OS !== 'web') return children;
  return (
    <Box
      style={styles.anchor}
      {...({
        onMouseEnter: () => setHovered(true),
        onMouseLeave: () => setHovered(false),
      } as any)}>
      {children}
      {open &&
        (customContent ? (
          <Box style={styles.linkCopiedBubble}>{customContent}</Box>
        ) : (
          <Box style={styles.tooltipBubble}>
            <Text style={styles.tooltipTxt}>{label}</Text>
          </Box>
        ))}
    </Box>
  );
};

const styles = StyleSheet.create({
  // The anchor wraps the button that opens the tooltip. It carries
  // userSelect: 'none' so a rapid double-click on the button never *starts* a
  // native text selection (TA-10615). Making only the bubbles unselectable
  // moves the highlight to whatever selectable text sits next to the button
  // (e.g. MediaViewer's `{zoom}%` label) instead of removing it.
  // Neither Platform-gated nor cast, unlike the bubbles below: userSelect is a
  // valid RN style on every platform, so there is nothing to guard against,
  // and both keys type-check as-is (RN declares userSelect on TextStyle, which
  // extends ViewStyle). Keeping it uncast means a typo in either value is
  // still caught.
  anchor: {
    position: 'relative',
    userSelect: 'none',
  },
  // The two bubbles below stay Platform-gated and cast: unlike userSelect, the
  // keys in those blocks (whiteSpace, a CSS-string boxShadow,
  // width: 'max-content') are not valid RN styles, so they must not reach a
  // native StyleSheet and cannot be expressed without the cast.
  tooltipBubble: {
    position: 'absolute',
    top: 40,
    right: 0,
    backgroundColor: theme.colors.black,
    borderRadius: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    zIndex: 9999,
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: `0 2px 8px ${theme.colors.scrimDark25}`,
          whiteSpace: 'nowrap',
          width: 'max-content',
          pointerEvents: 'none',
          userSelect: 'none',
        } as any)
      : {}),
  },
  tooltipTxt: {
    color: theme.colors.white,
    fontSize: 12,
  },
  // Renders whatever `customContent` the caller passes (e.g. MediaViewer's
  // "Link copied!" row) — the caller styles its own content; this bubble
  // only provides the anchored white pill it sits in.
  linkCopiedBubble: {
    position: 'absolute',
    top: 40,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.grey03,
    paddingHorizontal: theme.spacing.s,
    paddingVertical: 7,
    zIndex: 9999,
    ...(Platform.OS === 'web'
      ? ({
          boxShadow: `0 2px 8px ${theme.colors.scrimDark25}`,
          whiteSpace: 'nowrap',
          width: 'max-content',
          pointerEvents: 'none',
          userSelect: 'none',
        } as any)
      : {}),
  },
});
